import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import EmptyState from '../../../components/ui/EmptyState';
import InvoiceDownloadButton from '../../../components/ui/InvoiceDownloadButton';
import { supabase } from '../../../lib/supabase';
import DisputeSubmissionDrawer from './DisputeSubmissionDrawer';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatPrice = (price) => {
  if (!price) return '—';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'XOF',
    maximumFractionDigits: 0,
  })?.format(price);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })?.format(new Date(dateStr));
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })?.format(new Date(dateStr));
};

// ─── Escrow Status Config ─────────────────────────────────────────────────────

const ESCROW_STATUS_CONFIG = {
  DRAFT:            { label: 'Brouillon',         bg: 'bg-gray-50',        text: 'text-gray-500',    border: 'border-gray-200',   icon: 'FileText',      step: 0 },
  OFFER_SENT:       { label: 'Offre envoyée',      bg: 'bg-blue-50',        text: 'text-blue-600',    border: 'border-blue-100',   icon: 'Send',          step: 1 },
  OFFER_ACCEPTED:   { label: 'Offre acceptée',     bg: 'bg-indigo-50',      text: 'text-indigo-600',  border: 'border-indigo-100', icon: 'CheckCircle',   step: 2 },
  PAYMENT_PENDING:  { label: 'Paiement en attente',bg: 'bg-amber-50',       text: 'text-amber-600',   border: 'border-amber-100',  icon: 'Clock',         step: 3 },
  FUNDS_HELD:       { label: 'Fonds en séquestre', bg: 'bg-brand-gold/10',  text: 'text-brand-gold',  border: 'border-brand-gold/20', icon: 'Lock',        step: 4 },
  PENDING:          { label: 'En attente',         bg: 'bg-amber-50',       text: 'text-amber-600',   border: 'border-amber-100',  icon: 'Clock',         step: 3 },
  RELEASED:         { label: 'Fonds libérés',      bg: 'bg-emerald-50',     text: 'text-emerald-600', border: 'border-emerald-100',icon: 'Unlock',        step: 5 },
  COMPLETED:        { label: 'Complété',           bg: 'bg-emerald-50',     text: 'text-emerald-700', border: 'border-emerald-100',icon: 'CheckCircle2',  step: 6 },
  DISPUTE:          { label: 'Litige',             bg: 'bg-red-50',         text: 'text-red-600',     border: 'border-red-100',    icon: 'AlertTriangle', step: -1 },
  DISPUTED:         { label: 'Litige',             bg: 'bg-red-50',         text: 'text-red-600',     border: 'border-red-100',    icon: 'AlertTriangle', step: -1 },
  REFUNDED:         { label: 'Remboursé',          bg: 'bg-purple-50',      text: 'text-purple-600',  border: 'border-purple-100', icon: 'RotateCcw',     step: -1 },
  CANCELLED:        { label: 'Annulé',             bg: 'bg-red-50',         text: 'text-red-500',     border: 'border-red-100',    icon: 'XCircle',       step: -1 },
  SHIPPED:          { label: 'En cours',           bg: 'bg-blue-50',        text: 'text-blue-600',    border: 'border-blue-100',   icon: 'Truck',         step: 4 },
  DELIVERED:        { label: 'Livré',              bg: 'bg-teal-50',        text: 'text-teal-600',    border: 'border-teal-100',   icon: 'Package',       step: 5 },
  pending:          { label: 'En attente',         bg: 'bg-amber-50',       text: 'text-amber-600',   border: 'border-amber-100',  icon: 'Clock',         step: 3 },
};

const PAYMENT_STATUS_CONFIG = {
  success:   { label: 'Payé',           bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-100', icon: 'CheckCircle' },
  SUCCESS:   { label: 'Payé',           bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-100', icon: 'CheckCircle' },
  pending:   { label: 'En attente',     bg: 'bg-amber-50',   text: 'text-amber-600',   border: 'border-amber-100',   icon: 'Clock' },
  PENDING:   { label: 'En attente',     bg: 'bg-amber-50',   text: 'text-amber-600',   border: 'border-amber-100',   icon: 'Clock' },
  failed:    { label: 'Échoué',         bg: 'bg-red-50',     text: 'text-red-600',     border: 'border-red-100',     icon: 'XCircle' },
  FAILED:    { label: 'Échoué',         bg: 'bg-red-50',     text: 'text-red-600',     border: 'border-red-100',     icon: 'XCircle' },
  refunded:  { label: 'Remboursé',      bg: 'bg-purple-50',  text: 'text-purple-600',  border: 'border-purple-100',  icon: 'RotateCcw' },
};

const ESCROW_STEPS = [
  { key: 'OFFER_SENT',      label: 'Offre' },
  { key: 'OFFER_ACCEPTED',  label: 'Acceptée' },
  { key: 'FUNDS_HELD',      label: 'Séquestre' },
  { key: 'RELEASED',        label: 'Libéré' },
  { key: 'COMPLETED',       label: 'Complété' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

const LoadingSkeleton = ({ rows = 3 }) => (
  <div className="space-y-3">
    {Array.from({ length: rows })?.map((_, i) => (
      <div key={i} className="animate-pulse flex items-center gap-3 p-3 rounded-xl bg-surface/60">
        <div className="w-12 h-12 rounded-xl bg-border/50 flex-shrink-0" />
        <div className="flex-1 space-y-2">
          <div className="h-3.5 bg-border/50 rounded-lg w-3/4" />
          <div className="h-3 bg-border/40 rounded-lg w-1/2" />
        </div>
        <div className="h-5 w-16 bg-border/40 rounded-lg" />
      </div>
    ))}
  </div>
);

const EscrowStatusBadge = ({ status }) => {
  const cfg = ESCROW_STATUS_CONFIG?.[status] || ESCROW_STATUS_CONFIG?.DRAFT;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-semibold border ${cfg?.bg} ${cfg?.text} ${cfg?.border}`}>
      <Icon name={cfg?.icon} size={10} />
      {cfg?.label}
    </span>
  );
};

const PaymentStatusBadge = ({ status }) => {
  const cfg = PAYMENT_STATUS_CONFIG?.[status] || PAYMENT_STATUS_CONFIG?.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-semibold border ${cfg?.bg} ${cfg?.text} ${cfg?.border}`}>
      <Icon name={cfg?.icon} size={10} />
      {cfg?.label}
    </span>
  );
};

const EscrowProgressBar = ({ status }) => {
  const currentStep = ESCROW_STATUS_CONFIG?.[status]?.step ?? 0;
  if (currentStep < 0) return null;
  return (
    <div className="flex items-center gap-1 mt-2">
      {ESCROW_STEPS?.map((step, idx) => {
        const stepNum = idx + 1;
        const isCompleted = currentStep >= stepNum;
        const isCurrent = currentStep === stepNum;
        return (
          <React.Fragment key={step?.key}>
            <div className={`flex flex-col items-center gap-0.5 flex-shrink-0`}>
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold transition-colors ${
                isCompleted ? 'bg-brand-earth text-white' : isCurrent ? 'bg-brand-gold text-white' : 'bg-border/50 text-text-secondary'
              }`}>
                {isCompleted ? <Icon name="Check" size={9} /> : stepNum}
              </div>
              <span className="text-[8px] text-text-secondary whitespace-nowrap hidden sm:block">{step?.label}</span>
            </div>
            {idx < ESCROW_STEPS?.length - 1 && (
              <div className={`flex-1 h-0.5 rounded-full transition-colors ${currentStep > stepNum ? 'bg-brand-earth' : 'bg-border/40'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ─── Section Card (matches mon-espace style) ──────────────────────────────────

const SectionCard = ({ icon, iconBg = 'bg-brand-earth/10', iconColor = 'text-brand-earth', title, subtitle, count, children, ctaPath = null, ctaLabel = null }) => (
  <div className="bg-white dark:bg-card rounded-2xl shadow-ios-light border border-border/50 overflow-hidden">
    <div className="flex items-center justify-between px-5 py-4 border-b border-border/40">
      <div className="flex items-center space-x-3">
        <div className={`w-9 h-9 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
          <Icon name={icon} size={18} className={iconColor} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <p className="text-ios-callout font-body font-semibold text-text-primary">{title}</p>
            {count !== undefined && count > 0 && (
              <span className="px-2 py-0.5 bg-brand-gold/15 text-brand-gold text-[11px] font-bold rounded-full">
                {count}
              </span>
            )}
          </div>
          {subtitle && <p className="text-ios-caption1 text-text-secondary mt-0.5">{subtitle}</p>}
        </div>
      </div>
      {ctaPath && ctaLabel && (
        <Link
          to={ctaPath}
          className="flex items-center gap-1 text-xs font-medium text-brand-earth hover:text-brand-earth/80 transition-colors"
        >
          <span>{ctaLabel}</span>
          <Icon name="ArrowRight" size={12} />
        </Link>
      )}
    </div>
    <div className="px-5 py-5">{children}</div>
  </div>
);

// ─── Webhook Live Update Banner ───────────────────────────────────────────────
const WebhookLiveBanner = ({ recentEvents }) => {
  if (!recentEvents?.length) return null;
  const latest = recentEvents?.[0];
  const eventLabels = {
    'charge.success': { label: 'Paiement confirmé', color: 'bg-emerald-50 border-emerald-200 text-emerald-700', icon: 'CheckCircle' },
    'charge.dispute.create': { label: 'Litige créé', color: 'bg-red-50 border-red-200 text-red-700', icon: 'AlertTriangle' },
    'charge.dispute.resolve': { label: 'Litige résolu', color: 'bg-blue-50 border-blue-200 text-blue-700', icon: 'CheckCircle' },
    'refund.processed': { label: 'Remboursement traité', color: 'bg-purple-50 border-purple-200 text-purple-700', icon: 'RotateCcw' },
    'transfer.success': { label: 'Virement effectué', color: 'bg-green-50 border-green-200 text-green-700', icon: 'ArrowUpRight' },
  };
  const cfg = eventLabels?.[latest?.event_type] || { label: latest?.event_type, color: 'bg-gray-50 border-gray-200 text-gray-700', icon: 'Activity' };
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-medium ${cfg?.color}`}>
      <span className="w-2 h-2 rounded-full bg-current animate-pulse flex-shrink-0" />
      <Icon name={cfg?.icon} size={12} />
      <span>Mise à jour en direct : <strong>{cfg?.label}</strong></span>
      <span className="ml-auto text-[10px] opacity-60">
        {latest?.created_at ? new Date(latest?.created_at)?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
      </span>
    </div>
  );
};

// ─── Past Transactions Section ────────────────────────────────────────────────

const PastTransactionsSection = ({ userId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [disputeDrawerTx, setDisputeDrawerTx] = useState(null);
  const [recentWebhookEvents, setRecentWebhookEvents] = useState([]);

  const fetchTransactions = useCallback(async () => {
    if (!userId) return;
    try {
      const { data, error } = await supabase
        ?.from('payments')
        ?.select(`
          id,
          session_id,
          amount,
          status,
          paystack_reference,
          created_at,
          updated_at,
          property_id,
          transaction_id,
          property:properties(id, title, address, property_type)
        `)
        ?.eq('user_id', userId)
        ?.order('created_at', { ascending: false })
        ?.limit(10);

      if (!error) setItems(data || []);
    } catch (_) {}
  }, [userId]);

  // Fetch recent webhook events for this user's payments
  const fetchWebhookEvents = useCallback(async () => {
    if (!userId) return;
    try {
      const { data } = await supabase
        ?.from('paystack_webhook_events')
        ?.select('id, event_type, reference, created_at, processed')
        ?.order('created_at', { ascending: false })
        ?.limit(5);
      setRecentWebhookEvents(data || []);
    } catch (_) {}
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      setLoading(true);
      await Promise.all([fetchTransactions(), fetchWebhookEvents()]);
      setLoading(false);
    })();
  }, [userId, fetchTransactions, fetchWebhookEvents]);

  // Realtime subscription for payments and webhook events
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      ?.channel(`payments_user_${userId}`)
      ?.on('postgres_changes', { event: '*', schema: 'public', table: 'payments', filter: `user_id=eq.${userId}` }, () => {
        fetchTransactions();
      })
      ?.on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'paystack_webhook_events' }, () => {
        fetchWebhookEvents();
        fetchTransactions();
      })
      ?.subscribe();
    return () => { supabase?.removeChannel(channel); };
  }, [userId, fetchTransactions, fetchWebhookEvents]);

  const toggleExpand = (id) => setExpanded(prev => prev === id ? null : id);

  return (
    <>
    <SectionCard
      icon="Receipt"
      iconBg="bg-emerald-50"
      iconColor="text-emerald-600"
      title="Transactions passées"
      subtitle="Historique de vos paiements"
      count={items?.length}
    >
      {/* Live webhook update banner */}
      {recentWebhookEvents?.length > 0 && (
        <div className="mb-3">
          <WebhookLiveBanner recentEvents={recentWebhookEvents} />
        </div>
      )}

      {loading ? (
        <LoadingSkeleton rows={3} />
      ) : items?.length === 0 ? (
        <EmptyState
          iconName="Receipt"
          title="Aucune transaction"
          description="Vos transactions de paiement apparaîtront ici après votre premier achat."
          ctaLabel="Voir les propriétés"
          ctaIcon="Home"
          onCta={() => window.location.href = '/property-search-listings'}
          size="sm"
        />
      ) : (
        <div className="space-y-2.5">
          {items?.map((tx) => (
            <div key={tx?.id} className="rounded-xl border border-border/40 overflow-hidden">
              <button
                onClick={() => toggleExpand(tx?.id)}
                className="w-full flex items-center gap-3 p-3 hover:bg-surface/60 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <Icon name="CreditCard" size={16} className="text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-text-primary truncate">
                    {tx?.property?.title || tx?.session_id || 'Transaction'}
                  </p>
                  <p className="text-xs text-text-secondary mt-0.5">{formatDate(tx?.created_at)}</p>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className="text-sm font-bold text-brand-earth">{formatPrice(tx?.amount)}</span>
                  <PaymentStatusBadge status={tx?.status} />
                </div>
                <Icon name={expanded === tx?.id ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-text-secondary ml-1 flex-shrink-0" />
              </button>

              {expanded === tx?.id && (
                <div className="px-4 pb-4 pt-1 bg-surface/30 border-t border-border/30 space-y-2">
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {tx?.paystack_reference && (
                      <div>
                        <p className="text-text-secondary">Référence Paystack</p>
                        <p className="font-mono font-semibold text-text-primary truncate">{tx?.paystack_reference}</p>
                      </div>
                    )}
                    {tx?.session_id && (
                      <div>
                        <p className="text-text-secondary">ID Session</p>
                        <p className="font-mono font-semibold text-text-primary truncate">{tx?.session_id?.slice(0, 16)}…</p>
                      </div>
                    )}
                    {tx?.property?.address && (
                      <div className="col-span-2">
                        <p className="text-text-secondary">Adresse</p>
                        <p className="font-semibold text-text-primary">{tx?.property?.address}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-text-secondary">Montant</p>
                      <p className="font-bold text-brand-earth">{formatPrice(tx?.amount)}</p>
                    </div>
                    <div>
                      <p className="text-text-secondary">Date</p>
                      <p className="font-semibold text-text-primary">{formatDateTime(tx?.created_at)}</p>
                    </div>
                  </div>
                  {/* Invoice download */}
                  <div className="pt-2 border-t border-border/30">
                    <InvoiceDownloadButton transaction={tx} type="payment" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </SectionCard>

    {/* Dispute Submission Drawer */}
    {disputeDrawerTx && (
      <DisputeSubmissionDrawer
        transaction={disputeDrawerTx}
        onClose={() => setDisputeDrawerTx(null)}
        onDisputeSubmitted={() => { fetchTransactions(); setDisputeDrawerTx(null); }}
      />
    )}
    </>
  );
};

// ─── Escrow Status Section ────────────────────────────────────────────────────

const EscrowStatusSection = ({ userId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [disputeDrawerTx, setDisputeDrawerTx] = useState(null);
  const [dismissedNotaryBanners, setDismissedNotaryBanners] = useState(() => {
    try {
      return JSON.parse(localStorage?.getItem('dismissed_notary_banners') || '[]');
    } catch { return []; }
  });

  const fetchEscrow = useCallback(async () => {
    if (!userId) return;
    try {
      const { data, error } = await supabase
        ?.from('escrow_transactions')
        ?.select(`
          id,
          title,
          item_title,
          amount_total,
          total_amount,
          kps_commission_amount,
          seller_payout_amount,
          seller_amount,
          platform_fee,
          currency,
          status,
          paystack_reference,
          reference,
          escrow_release_date,
          notary_step_prompted,
          created_at,
          updated_at,
          property_id,
          property:properties(id, title, address, property_type)
        `)
        ?.eq('buyer_id', userId)
        ?.order('created_at', { ascending: false })
        ?.limit(10);

      if (!error) setItems(data || []);
    } catch (_) {}
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      setLoading(true);
      await fetchEscrow();
      setLoading(false);
    })();
  }, [userId, fetchEscrow]);

  // Subscribe to realtime changes on escrow_transactions for this buyer
  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      ?.channel(`escrow_buyer_${userId}`)
      ?.on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'escrow_transactions',
        filter: `buyer_id=eq.${userId}`,
      }, () => {
        fetchEscrow();
      })
      ?.subscribe();

    return () => { supabase?.removeChannel(channel); };
  }, [userId, fetchEscrow]);

  const toggleExpand = (id) => setExpanded(prev => prev === id ? null : id);

  const handleDismissNotaryBanner = (escrowId) => {
    const updated = [...dismissedNotaryBanners, escrowId];
    setDismissedNotaryBanners(updated);
    try { localStorage?.setItem('dismissed_notary_banners', JSON.stringify(updated)); } catch (_) {}
  };

  const activeCount = items?.filter(e => !['COMPLETED', 'CANCELLED', 'REFUNDED']?.includes(e?.status))?.length;

  // Released escrows that need notary step surfaced
  const releasedPendingNotary = items?.filter(e =>
    e?.status === 'RELEASED' && !dismissedNotaryBanners?.includes(e?.id)
  );

  return (
    <>
      {/* Notary step banners for released escrows */}
      {releasedPendingNotary?.length > 0 && (
        <div className="space-y-3 mb-1">
          {releasedPendingNotary?.map(escrow => (
            <NextNotaryStepBanner
              key={escrow?.id}
              escrow={escrow}
              onDismiss={() => handleDismissNotaryBanner(escrow?.id)}
            />
          ))}
        </div>
      )}

      <SectionCard
        icon="Lock"
        iconBg="bg-brand-gold/10"
        iconColor="text-brand-gold"
        title="Statut du séquestre"
        subtitle="Suivi de vos fonds en séquestre"
        count={activeCount}
      >
        {loading ? (
          <LoadingSkeleton rows={3} />
        ) : items?.length === 0 ? (
          <EmptyState
            iconName="Lock"
            title="Aucun séquestre actif"
            description="Vos transactions en séquestre apparaîtront ici lors de vos achats immobiliers."
            ctaLabel="Voir les propriétés"
            ctaIcon="Building2"
            onCta={() => window.location.href = '/property-search-listings'}
            size="sm"
          />
        ) : (
          <div className="space-y-2.5">
            {items?.map((escrow) => {
              const displayTitle = escrow?.item_title || escrow?.title || escrow?.property?.title || 'Séquestre';
              const displayAmount = escrow?.total_amount || escrow?.amount_total;
              const isReleased = escrow?.status === 'RELEASED';
              return (
                <div key={escrow?.id} className="rounded-xl border border-border/40 overflow-hidden">
                  <button
                    onClick={() => toggleExpand(escrow?.id)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-surface/60 transition-colors text-left"
                  >
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isReleased ? 'bg-emerald-50' : 'bg-brand-gold/10'}`}>
                      <Icon name={isReleased ? 'Unlock' : 'ShieldCheck'} size={16} className={isReleased ? 'text-emerald-600' : 'text-brand-gold'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-text-primary truncate">{displayTitle}</p>
                      <p className="text-xs text-text-secondary mt-0.5">{formatDate(escrow?.created_at)}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="text-sm font-bold text-brand-earth">{formatPrice(displayAmount)}</span>
                      <EscrowStatusBadge status={escrow?.status} />
                    </div>
                    <Icon name={expanded === escrow?.id ? 'ChevronUp' : 'ChevronDown'} size={14} className="text-text-secondary ml-1 flex-shrink-0" />
                  </button>

                  {expanded === escrow?.id && (
                    <div className="px-4 pb-4 pt-2 bg-surface/30 border-t border-border/30 space-y-3">
                      {/* Progress bar */}
                      <EscrowProgressBar status={escrow?.status} />

                      {/* Inline notary step prompt for released escrow */}
                      {isReleased && (
                        <div className="flex items-center gap-2 p-2.5 rounded-lg bg-indigo-50 border border-indigo-100">
                          <Icon name="Scale" size={14} className="text-indigo-600 flex-shrink-0" />
                          <p className="text-xs text-indigo-700 flex-1">
                            <span className="font-semibold">Fonds libérés.</span> Planifiez votre rendez-vous notaire.
                          </p>
                          <Link
                            to="/notary-appointment-booking-system"
                            className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 whitespace-nowrap flex items-center gap-1"
                            onClick={e => e?.stopPropagation()}
                          >
                            Prendre RDV <Icon name="ArrowRight" size={10} />
                          </Link>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                        <div>
                          <p className="text-text-secondary">Montant total</p>
                          <p className="font-bold text-brand-earth">{formatPrice(displayAmount)}</p>
                        </div>
                        {(escrow?.kps_commission_amount || escrow?.platform_fee) && (
                          <div>
                            <p className="text-text-secondary">Commission KPS</p>
                            <p className="font-semibold text-text-primary">{formatPrice(escrow?.kps_commission_amount || escrow?.platform_fee)}</p>
                          </div>
                        )}
                        {(escrow?.seller_payout_amount || escrow?.seller_amount) && (
                          <div>
                            <p className="text-text-secondary">Montant vendeur</p>
                            <p className="font-semibold text-text-primary">{formatPrice(escrow?.seller_payout_amount || escrow?.seller_amount)}</p>
                          </div>
                        )}
                        {escrow?.escrow_release_date && (
                          <div>
                            <p className="text-text-secondary">Date de libération</p>
                            <p className="font-semibold text-text-primary">{formatDate(escrow?.escrow_release_date)}</p>
                          </div>
                        )}
                        {(escrow?.paystack_reference || escrow?.reference) && (
                          <div className="col-span-2">
                            <p className="text-text-secondary">Référence</p>
                            <p className="font-mono font-semibold text-text-primary truncate">{escrow?.paystack_reference || escrow?.reference}</p>
                          </div>
                        )}
                        {escrow?.property?.address && (
                          <div className="col-span-2">
                            <p className="text-text-secondary">Propriété</p>
                            <p className="font-semibold text-text-primary">{escrow?.property?.address}</p>
                          </div>
                        )}
                      </div>

                      {/* Dispute action button */}
                      <div className="pt-1 border-t border-border/30">
                        <button
                          onClick={(e) => { e?.stopPropagation(); setDisputeDrawerTx(escrow); }}
                          className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                            ['DISPUTE', 'DISPUTED']?.includes(escrow?.status)
                              ? 'bg-red-50 text-red-600 border border-red-200 hover:bg-red-100' :'bg-surface text-text-secondary border border-border/40 hover:bg-red-50 hover:text-red-600 hover:border-red-200'
                          }`}
                        >
                          <Icon name="AlertTriangle" size={12} />
                          {['DISPUTE', 'DISPUTED']?.includes(escrow?.status)
                            ? 'Voir le litige en cours' :'Signaler un problème / Litige'}
                        </button>
                      </div>
                      {/* Invoice download */}
                      <div className="pt-1">
                        <InvoiceDownloadButton transaction={escrow} type="escrow" />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      {/* Dispute Submission Drawer */}
      {disputeDrawerTx && (
        <DisputeSubmissionDrawer
          transaction={disputeDrawerTx}
          onClose={() => setDisputeDrawerTx(null)}
          onDisputeSubmitted={() => { fetchEscrow(); setDisputeDrawerTx(null); }}
        />
      )}
    </>
  );
};

// ─── Next Notary Step Banner ──────────────────────────────────────────────────
const NextNotaryStepBanner = ({ escrow, onDismiss }) => {
  const displayTitle = escrow?.item_title || escrow?.title || escrow?.property?.title || 'votre propriété';
  const ref = escrow?.reference || escrow?.paystack_reference || escrow?.id?.slice(0, 8);

  return (
    <div className="relative rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50 p-4 shadow-sm overflow-hidden">
      {/* Decorative accent */}
      <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500 rounded-l-xl" />
      <div className="pl-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-indigo-100 flex items-center justify-center flex-shrink-0">
              <Icon name="Scale" size={16} className="text-indigo-600" />
            </div>
            <div>
              <p className="text-sm font-bold text-indigo-900">Prochaine étape : Rendez-vous notaire</p>
              <p className="text-xs text-indigo-600 mt-0.5">Fonds libérés · Réf. {ref}</p>
            </div>
          </div>
          {onDismiss && (
            <button
              onClick={onDismiss}
              className="text-indigo-300 hover:text-indigo-500 transition-colors flex-shrink-0 mt-0.5"
              aria-label="Fermer"
            >
              <Icon name="X" size={14} />
            </button>
          )}
        </div>
        <p className="text-xs text-indigo-700 mt-2 leading-relaxed">
          Les fonds séquestre pour <span className="font-semibold">{displayTitle}</span> ont été libérés.
          Planifiez maintenant votre rendez-vous chez le notaire pour finaliser l'acte de vente.
        </p>
        <div className="flex items-center gap-2 mt-3">
          <Link
            to="/notary-appointment-booking-system"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Icon name="Calendar" size={12} />
            Prendre rendez-vous
          </Link>
          <Link
            to="/notary-appointment-booking-system"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white text-indigo-600 text-xs font-semibold rounded-lg border border-indigo-200 hover:bg-indigo-50 transition-colors"
          >
            <Icon name="Search" size={12} />
            Trouver un notaire
          </Link>
        </div>
      </div>
    </div>
  );
};

// ─── Notary Appointments Section ──────────────────────────────────────────────

const NotaryAppointmentsSection = ({ userId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotaryAppointments = useCallback(async () => {
    if (!userId) return;
    try {
      const { data, error } = await supabase
        ?.from('appointments')
        ?.select(`
          id,
          appointment_type,
          scheduled_date,
          status,
          notes,
          location,
          created_at,
          property:properties(id, title, address, property_type),
          notary:notaries(id, notary_name, office_address, license_number)
        `)
        ?.eq('client_id', userId)
        ?.not('notary_id', 'is', null)
        ?.in('status', ['scheduled', 'confirmed', 'pending'])
        ?.order('scheduled_date', { ascending: true })
        ?.limit(8);

      if (!error) setItems(data || []);
    } catch (_) {}
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      setLoading(true);
      await fetchNotaryAppointments();
      setLoading(false);
    })();
  }, [userId, fetchNotaryAppointments]);

  const APPT_STATUS = {
    scheduled:   { label: 'Planifié',    bg: 'bg-blue-50',   text: 'text-blue-700',   border: 'border-blue-100' },
    confirmed:   { label: 'Confirmé',    bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-100' },
    pending:     { label: 'En attente',  bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-100' },
  };

  return (
    <SectionCard
      icon="Scale"
      iconBg="bg-indigo-50"
      iconColor="text-indigo-600"
      title="Rendez-vous notaires en attente"
      subtitle="Vos prochains rendez-vous chez le notaire"
      count={items?.length}
      ctaPath="/notary-appointment-booking-system"
      ctaLabel="Prendre RDV"
    >
      {loading ? (
        <LoadingSkeleton rows={3} />
      ) : items?.length === 0 ? (
        <EmptyState
          iconName="Scale"
          title="Aucun rendez-vous notaire"
          description="Planifiez un rendez-vous avec un notaire pour finaliser votre transaction."
          ctaLabel="Trouver un notaire"
          ctaIcon="Search"
          onCta={() => window.location.href = '/notary-appointment-booking-system'}
          size="sm"
        />
      ) : (
        <div className="space-y-2.5">
          {items?.map((appt) => {
            const statusCfg = APPT_STATUS?.[appt?.status] || APPT_STATUS?.pending;
            const isUrgent = appt?.scheduled_date && new Date(appt?.scheduled_date) - new Date() < 48 * 3600 * 1000;
            return (
              <div
                key={appt?.id}
                className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${
                  isUrgent ? 'bg-amber-50/50 border-amber-200/60' : 'bg-surface/50 border-border/30'
                }`}
              >
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isUrgent ? 'bg-amber-100' : 'bg-indigo-50'}`}>
                  <Icon name="Scale" size={16} className={isUrgent ? 'text-amber-600' : 'text-indigo-600'} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-text-primary truncate">
                      {appt?.notary?.notary_name || 'Notaire'}
                    </p>
                    {isUrgent && (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-full border border-amber-200">
                        <Icon name="AlertCircle" size={9} />
                        Bientôt
                      </span>
                    )}
                  </div>
                  {appt?.property?.title && (
                    <p className="text-xs text-brand-earth font-medium mt-0.5 truncate flex items-center gap-1">
                      <Icon name="Building2" size={10} />
                      {appt?.property?.title}
                    </p>
                  )}
                  <p className="text-xs text-text-secondary mt-0.5 flex items-center gap-1">
                    <Icon name="Calendar" size={10} />
                    {formatDateTime(appt?.scheduled_date)}
                  </p>
                  {(appt?.notary?.office_address || appt?.location) && (
                    <p className="text-xs text-text-secondary mt-0.5 flex items-center gap-1 truncate">
                      <Icon name="MapPin" size={10} />
                      {appt?.notary?.office_address || appt?.location}
                    </p>
                  )}
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-lg text-[11px] font-semibold border flex-shrink-0 ${statusCfg?.bg} ${statusCfg?.text} ${statusCfg?.border}`}>
                  {statusCfg?.label}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
};

// ─── Payment Receipts Section ─────────────────────────────────────────────────

const PaymentReceiptsSection = ({ userId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReceipts = useCallback(async () => {
    if (!userId) return;
    try {
      const { data, error } = await supabase
        ?.from('payments')
        ?.select(`
          id,
          session_id,
          amount,
          status,
          paystack_reference,
          created_at,
          property_id,
          property:properties(id, title, property_type)
        `)
        ?.eq('user_id', userId)
        ?.eq('status', 'success')
        ?.order('created_at', { ascending: false })
        ?.limit(8);

      if (!error) setItems(data || []);
    } catch (_) {}
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    (async () => {
      setLoading(true);
      await fetchReceipts();
      setLoading(false);
    })();
  }, [userId, fetchReceipts]);

  return (
    <SectionCard
      icon="FileCheck"
      iconBg="bg-teal-50"
      iconColor="text-teal-600"
      title="Reçus de paiement"
      subtitle="Vos confirmations de paiement réussi"
      count={items?.length}
    >
      {loading ? (
        <LoadingSkeleton rows={3} />
      ) : items?.length === 0 ? (
        <EmptyState
          iconName="FileCheck"
          title="Aucun reçu disponible"
          description="Vos reçus de paiement confirmé apparaîtront ici."
          size="sm"
        />
      ) : (
        <div className="space-y-2.5">
          {items?.map((receipt) => (
            <div
              key={receipt?.id}
              className="flex items-center gap-3 p-3 rounded-xl bg-teal-50/40 border border-teal-100/60"
            >
              <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
                <Icon name="FileCheck" size={16} className="text-teal-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-text-primary truncate">
                  {receipt?.property?.title || 'Paiement confirmé'}
                </p>
                <p className="text-xs text-text-secondary mt-0.5 font-mono truncate">
                  {receipt?.paystack_reference || receipt?.session_id?.slice(0, 20) || receipt?.id?.slice(0, 12)}
                </p>
                <p className="text-xs text-text-secondary mt-0.5">{formatDate(receipt?.created_at)}</p>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className="text-sm font-bold text-teal-700">{formatPrice(receipt?.amount)}</span>
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-100">
                  <Icon name="CheckCircle" size={10} />
                  Payé
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
};

// ─── Main Dashboard Component ─────────────────────────────────────────────────

const UserTransactionDashboard = ({ userId }) => {
  if (!userId) return null;

  return (
    <div className="space-y-6">
      {/* Section header */}
      <div className="flex items-center gap-3 px-1">
        <div className="w-8 h-8 rounded-xl bg-brand-earth/10 flex items-center justify-center">
          <Icon name="LayoutDashboard" size={16} className="text-brand-earth" />
        </div>
        <div>
          <h2 className="text-base font-heading font-bold text-text-primary">Tableau de bord transactions</h2>
          <p className="text-xs text-text-secondary">Suivi de vos achats, séquestres et rendez-vous notaires</p>
        </div>
      </div>

      {/* 2-column grid on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PastTransactionsSection userId={userId} />
        <EscrowStatusSection userId={userId} />
        <PaymentReceiptsSection userId={userId} />
        <NotaryAppointmentsSection userId={userId} />
      </div>
    </div>
  );
};

export default UserTransactionDashboard;
